import { API_URL } from '@/utils/env'
import { addMonths, endOfMonth, formatISO, subMonths } from 'date-fns'
import { useCallback } from 'react'
import type { ExportFormat } from '.'
import { ExportType } from './consts'
import type { ExportStatus, YearMonthExportData } from './types'
import type { UserExportNode } from './types.graphql'
import { useTranslate } from '@ur-solutions/react-hooks'

export const getExportDates = (
  exportType: ExportType,
  year: number,
  month: number
): [string, string] => {
  const [startDate, endDate] =
    exportType === ExportType.ENTRY_MONTH
      ? [new Date(year, month), endOfMonth(new Date(year, month))]
      : [subMonths(new Date(year, month, 26), 1), new Date(year, month, 25)]
  return [formatISO(startDate), formatISO(endDate)]
}

export const getExportLink = (
  data: YearMonthExportData,
  exportType: ExportType,
  dateStart: string,
  dateEnd: string,
  exportFormat: ExportFormat
): string => {
  return `${API_URL}/${exportType}/${data.companyId}/${data.userId}?date_start=${dateStart}&date_end=${dateEnd}&export_format=${exportFormat}`
}

export const getSalaryDatesForMonth = (
  dateStart: number,
  dateEnd: number,
  year: number,
  month: number
): [Date, Date] => {
  const safeMonth = month - 1 === 0 ? 12 : month - 1
  const startMonthDate =
    safeMonth === 12 ? new Date(year - 1, safeMonth) : new Date(year, safeMonth)
  const safeStartYear = startMonthDate.getFullYear()
  const safeStartMonth = startMonthDate.getMonth()
  const nextMonthDate = addMonths(startMonthDate, 1)

  const nextMonthEdgeDate = addMonths(
    new Date(safeStartYear, safeStartMonth),
    1
  )
  nextMonthEdgeDate.setDate(
    Math.min(dateEnd, endOfMonth(nextMonthDate).getDate())
  )

  const startDate =
    endOfMonth(startMonthDate).getDate() < dateStart
      ? addMonths(new Date(safeStartYear, safeStartMonth, 1), 1)
      : new Date(safeStartYear, safeStartMonth, dateStart)
  const endDate =
    dateStart === 1
      ? new Date(
          year,
          safeMonth,
          Math.min(dateEnd, endOfMonth(startMonthDate).getDate())
        )
      : nextMonthEdgeDate

  return [startDate, endDate]
}

export function useGetExportStatus() {
  const translations = useTranslate({
    fileNotFound: 'errors.file-not-found',
    unknownError: 'errors.unknown',
  })

  const getExportStatus = useCallback(
    (
      userExport:
        | Pick<UserExportNode, 'id' | 'exception' | 'progress' | 'file'>
        | null
        | undefined
    ): ExportStatus => {
      if (!userExport)
        return {
          status: 'pending',
          message: 0,
        }

      if (userExport.exception !== null && userExport.exception !== '')
        return {
          status: 'error',
          message: userExport.exception,
        }

      if (userExport.progress === -1)
        return {
          status: 'error',
          message: translations.unknownError,
        }

      if (userExport.progress < 100)
        return {
          status: 'pending',
          message: userExport.progress,
        }

      if (userExport.progress === 100 && !userExport.file)
        return {
          status: 'error',
          message: translations.fileNotFound,
        }

      if (userExport.progress === 100 && !!userExport.file)
        return {
          status: 'success',
          message: `${API_URL}/download-user-export/${userExport.id}`,
        }

      return {
        status: 'error',
        message: translations.unknownError,
      }
    },
    [translations.fileNotFound, translations.unknownError]
  )

  return getExportStatus
}
